import Moment from 'react-moment'
import { useState } from 'react'
import { MdLocationOn } from 'react-icons/md'
import { RiFeedbackFill } from 'react-icons/ri'

import { FaShare, FaTrash } from 'react-icons/fa'
import { IoIosAddCircleOutline } from 'react-icons/io'
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import { useEffect, useRef } from 'react'
import DeletePopupModal from './DeletePopupModal'
import FeedbackPopupModal from './FeedbackPopupModal'
import { FaCircleInfo } from 'react-icons/fa6'
import useAbility from '@/hooks/use-ability'
import UpgradeLayout from './upgrade-layout'
import UpgradeLockIconLayout from './upgrade-lock-icon-layout'
import { useRefSize } from '@/hooks/use-ref-size'

export default function ParcelCardView({
  page,
  id,
  onEdit,
  onParcelDelete,
  onRanchParcelDelete,
  property,
  onPropertyClicked,
  handlePropertyMouseOver,
  handlePropertyMouseOut,
  setLoading,
  isDemoUser,
  setPopupContent,
}) {
  const contentInnerRef = useRef(null)
  const listing = property.data
  const [deletePopupModal, setDeletePopupModal] = useState(false)
  const [feedbackPopupModal, setFeedbackPopupModal] = useState(false)
  const changeParagraphWidth = () => {
    const contentInnerElement = contentInnerRef.current
    if (contentInnerElement) {
      const contentInnerWidth =
        window.getComputedStyle(contentInnerElement).width
      const maxWidthPercentage = 80
      const maxWidthPixel =
        (parseFloat(contentInnerWidth) * maxWidthPercentage) / 100
      const paragraphs = contentInnerElement.querySelectorAll('p')
      paragraphs.forEach((paragraph) => {
        paragraph.style.maxWidth = `${maxWidthPixel}px`
      })
    }
  }
  useEffect(() => {
    window.addEventListener('resize', changeParagraphWidth, false)
    changeParagraphWidth()
  }, [])

  function hydraScoreColor(hydraScore) {
    if (hydraScore >= 93) return '#008000'
    else if (hydraScore >= 79) return '#8FB935'
    else if (hydraScore >= 68) return '#E6E22E'
    else if (hydraScore >= 48) return '#E09C3B'
    else if (hydraScore === 'NR') return '#808080'
    else return '#E64747'
  }
  
  const getHoverText = (feature) => {
      const wdHovers = feature.properties.wd_info
          .flatMap(wd => wd.wd_hover); // Extract wd_hover values

      const gsaHovers = feature.properties.gsa_info
          .flatMap(gsa => gsa.gsa_hover); // Extract gsa_hover values

      return [...wdHovers, ...gsaHovers].join("\n\n"); // Combine both with new lines
  };

  const { checkAbility } = useAbility()
  const enableOwnershipInformation = checkAbility('ownership_information')
  const enableHydraScore = checkAbility('hydra_score')
  const enableSoilScore = checkAbility('soil_score')
  const enableAPNDisplay = checkAbility('apn_display')
  const disableContentFeatures =
    !enableOwnershipInformation &&
    !enableHydraScore &&
    !enableSoilScore &&
    !enableAPNDisplay
  const disableViewOwnership = !enableOwnershipInformation && !enableAPNDisplay
  const layoutRef = useRef(null)
  const { width } = useRefSize(layoutRef)

  const enableCustomRanch = checkAbility('custom_ranches_portfolio')

  const AbilityLayoutComponent = () => {
    if (disableContentFeatures)
      return (
        <UpgradeLayout
          position="h-[195px]"
          className="h-[195px]"
          title="Upgrade plan to view content and more features."
          needUpgrade={true}
          onClickOutside={() => onPropertyClicked(property)}
        />
      )

    return (
      <>
        {disableViewOwnership && (
          <UpgradeLayout
            position="top-[176px] right-0 h-[52px] w-full px-3"
            className="min-h-[52px] flex flex-row items-center justify-center text-sm px-0"
            title={
              width > 250
                ? 'Upgrade to view Property Ownership information.'
                : ''
            }
            needUpgrade={true}
            onClickOutside={() => onPropertyClicked(property)}
          />
        )}
      </>
    )
  }

  return listing === undefined ? (
    <></>
  ) : (
    <div ref={layoutRef} className="relative">
      <AbilityLayoutComponent />
      {feedbackPopupModal && (
        <FeedbackPopupModal
          setFeedbackPopupModal={setFeedbackPopupModal}
          feedbackPopupModal={feedbackPopupModal}
          property={property}
          setLoading={setLoading}
        />
      )}
      {deletePopupModal && (
        <DeletePopupModal
          setDeletePopupModal={setDeletePopupModal}
          deletePopupModal={deletePopupModal}
          onDelete={onRanchParcelDelete}
          type={'Parcel'}
        />
      )}
      <li
        ref={contentInnerRef}
        className="parcel-cardview relative bg-white flex flex-col justify-between items-center shadow-md hover:shadow-xl rounded-md overflow-hidden transition-shadow duration-150 m-[10px] cursor-pointer"
        onMouseOver={() => handlePropertyMouseOver(property)}
        onMouseOut={() => handlePropertyMouseOut()}
        onClick={() => {
          onPropertyClicked(property)
        }}
      >
        <div className="contents" to={`/detail/${id}`}>
          {listing.imgUrls.length ? (
            <img
              className="h-[170px] max-w-[380px] w-full object-cover hover:scale-105 transition-scale duration-200 ease-in"
              loading="lazy"
              src={listing.imgUrls[0]}
            />
          ) : (
            <div>
              <br />
              <br />
            </div>
          )}
          {listing.timestamp ? (
            <Moment
              className="absolute top-2 left-2 bg-[#3377cc] text-white uppercase text-xs font-semibold rounded-md px-2 py-1 shadow-lg"
              fromNow
            >
              {listing.timestamp}
            </Moment>
          ) : (
            <></>
          )}
          <div className="w-full p-[10px]">
            <div className="flex items-center space-x-1 truncate">
              {(listing.address || disableViewOwnership) && (
                <MdLocationOn className="h-4 w-4 text-green-600" />
              )}
              <div className="font-semibold text-sm mb-[2px] text-gray-600 truncate">
                <p className={`${isDemoUser ? 'blur' : ''}`}>
                  {listing.address}
                </p>
              </div>
            </div>
            <p className="font-semibold m-0 text-xl truncate">
              <span className={`${isDemoUser ? 'blur' : ''}`}>
                {listing.name}
              </span>
            </p>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <div className="font-bold text-xs flex flex-row gap-1 ">
                  <p className="text-xs">APN:</p>
                  <div className="relative w-full">
                    <UpgradeLockIconLayout
                      className="absolute min-w-[60px] min-h-[16px]"
                      show={!enableAPNDisplay}
                    />
                    <p
                      className={`
                      text-xs
                      ${isDemoUser ? 'blur' : ''}`}
                    >
                      {listing.apn}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {listing.county && (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  <p className="font-bold text-xs">
                    County:{' '}
                    <span className={`${isDemoUser ? 'blur' : ''}`}>
                      {listing.county}
                    </span>
                  </p>
                </div>
              </div>
            )}
            {listing.gsa && (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  <p className="font-bold text-xs">
                    GSA:{' '}
                    <span className={`${isDemoUser ? 'blur' : ''}`}>
                      {listing.gsa.join(', ').replaceAll(',', ', ')}
                    </span>
                  </p>
                </div>
              </div>
            )}
            {listing.wd && (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  <p className="font-bold text-xs">
                    Water District:{' '}
                    <span className={`${isDemoUser ? 'blur' : ''}`}>
                      {listing.wd.join(', ').replaceAll(',', ', ')}
                    </span>
                  </p>
                </div>
              </div>
            )}
            {listing.acre && (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  <p className="font-bold text-xs">
                    Acreage:{' '}
                    <span className={`${isDemoUser ? 'blur' : ''}`}>
                      {listing.acre}
                    </span>
                  </p>
                </div>
              </div>
            )}
            {listing.hydraScore && (
              <div
                className="absolute top-2 right-2 w-[70px]"
                title={
                  enableHydraScore
                    ? getHoverText(property.feature)
                    : undefined
                }
              >
                <div className="relative">
                  <UpgradeLockIconLayout
                    className="h-full rounded-full items-center justify-center opacity-80"
                    iconSize={{ width: 24, height: 24 }}
                    show={!enableHydraScore && !disableContentFeatures}
                  />
                  <div>
                    <CircularProgressbarWithChildren
                      value={
                        listing.hydraScore === 'NR' ? 0 : listing.hydraScore
                      }
                      background
                      backgroundPadding={6}
                      styles={buildStyles({
                        backgroundColor: hydraScoreColor(listing.hydraScore),
                        pathColor: '#fff',
                        trailColor: 'transparent',
                      })}
                    >
                      <div className="text-white" style={{ fontSize: 9 }}>
                        Hydra
                      </div>

                      <div
                        className="text-white text-xs font-bold relative"
                        style={{ fontSize: 25 }}
                      >
                        <span className={`${isDemoUser ? 'blur' : ''}`}>
                          {listing.hydraScore}
                        </span>
                      </div>

                      <div className="text-white" style={{ fontSize: 9 }}>
                        Score
                      </div>
                    </CircularProgressbarWithChildren>
                    {enableHydraScore && (
                      <button
                        style={{
                          height: '20px',
                          width: '20px',
                          position: 'absolute',
                          top: '0px', // Adjust positioning based on appearance
                          right: '0px', // Adjust positioning based on appearance
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0,
                          cursor: 'pointer',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          border: '1px solid #00ac9c',
                          boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                          transition:
                            'box-shadow 0.3s ease, transform 0.3s ease',
                        }}
                        onClick={(e) => {
                          e.stopPropagation() // Prevents parent onClick from triggering
                          setPopupContent(getHoverText(property.feature))
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.boxShadow =
                            '0 4px 10px rgba(0, 0, 0, 0.3)')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.boxShadow =
                            '0 2px 5px rgba(0, 0, 0, 0.2)')
                        }
                      >
                        <FaCircleInfo color="#00ac9c" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}{' '}
            {listing.ss && (
              <div className={'absolute top-[85px] right-2 w-[70px]'}>
                <div className="relative">
                  <UpgradeLockIconLayout
                    className="h-full rounded-full items-center justify-center opacity-80"
                    iconSize={{ width: 24, height: 24 }}
                    show={!enableSoilScore && !disableContentFeatures}
                  />
                  <CircularProgressbarWithChildren
                    value={listing.ss === 'NR' ? 0 : listing.ss}
                    background
                    backgroundPadding={6}
                    styles={buildStyles({
                      backgroundColor: hydraScoreColor(listing.ss),
                      pathColor: '#fff',
                      trailColor: 'transparent',
                    })}
                  >
                    <div className="text-white" style={{ fontSize: 9 }}>
                      Soil
                    </div>
                    <strong
                      className="text-white text-xs"
                      style={{ fontSize: 25 }}
                    >
                      <span className={`${isDemoUser ? 'blur' : ''}`}>
                        {listing.ss}
                      </span>
                    </strong>
                    <div className="text-white" style={{ fontSize: 9 }}>
                      Score
                    </div>
                  </CircularProgressbarWithChildren>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="relative"></div>
        {page === '/portfolio' && (
          <FaTrash
            title="Remove this parcel from your ranch"
            className="absolute bottom-2 right-2 h-[14px] cursor-pointer text-red-500"
            onClick={(event) => {
              event.stopPropagation()
              setDeletePopupModal(true)
              // onRanchParcelDelete()
            }}
          />
        )}
        {property.type === 'aoi' && onParcelDelete && (
          <FaTrash
            title="Remove this parcel from the list"
            className="absolute bottom-2 right-2 h-[14px] cursor-pointer text-red-500"
            onClick={(event) => {
              event.stopPropagation()
              onParcelDelete()
            }}
          />
        )}
        {property.type === 'ranch' && onRanchParcelDelete && (
          <FaTrash
            title="Remove this parcel from your ranch"
            className="absolute bottom-2 right-2 h-[14px] cursor-pointer text-red-500"
            onClick={(event) => {
              event.stopPropagation()
              setDeletePopupModal(true)
              // onRanchParcelDelete()
            }}
          />
        )}
        <RiFeedbackFill
          title="Send us feedback on this parcel"
          className="absolute bottom-2 right-6 h-[14px] cursor-pointer text-blue-500"
          onClick={(event) => {
            event.stopPropagation()
            setFeedbackPopupModal(true)
            // onRanchParcelDelete()
          }}
        />
        <FaShare
          title="Share this parcel"
          className="absolute bottom-2 right-10 h-4 cursor-pointer "
          style={{
            color: '#00ac9c',
          }}
          onClick={(event) => {
            event.stopPropagation() // Prevent triggering the card's onClick
            navigator
              .share({
                title: 'Hydra score parcel(s)',
                text: 'Click link to see shared parcel(s)',
                url: `https://app.hydrascore.com/?locations=${btoa(
                  JSON.stringify([
                    {
                      lat: property.feature.properties.latitude,
                      lon: property.feature.properties.longitude,
                    },
                  ])
                )}`,
              })
              .catch((error) => {
                console.error('Sharing failed', error)
              })
          }}
        />

        {property.type === 'aoi' && onEdit && enableCustomRanch && (
          <IoIosAddCircleOutline
            title="Add this parcel to your ranch"
            className="absolute bottom-2 right-14 h-4 cursor-pointer "
            onClick={(event) => {
              event.stopPropagation()
              onEdit(listing.id)
            }}
          />
        )}
      </li>
    </div>
  )
}
